import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getArticles } from '../services/articlesService';
import socket, { initSocket } from '../services/socket';

const ArticleContext = createContext();

export const useArticles = () => {
  const context = useContext(ArticleContext);
  if (!context) {
    throw new Error('useArticles must be used within an ArticleProvider');
  }
  return context;
};

export const ArticleProvider = ({ children }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadArticles = useCallback(async () => {
    if (loading) {
      return articles;
    }
    
    setLoading(true);
    try {
      const result = await getArticles();
      if (result.success) {
        setArticles(result.data);
        return result.data;
      }
      return [];
    } catch (error) {
      console.error('Error loading articles:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [loading, articles]);

  const updateArticle = useCallback(async (articleId, updates) => {
    // Optimistic Update: Lokalen State sofort aktualisieren
    setArticles(prevArticles => 
      prevArticles.map(article => 
        article.id === articleId 
          ? { ...article, ...updates }
          : article
      )
    );
    
    // API-Call zum Backend
    try {
      const { updateArticle: updateArticleAPI } = await import('../services/articlesService');
      const result = await updateArticleAPI(articleId, updates);
      
      if (result.success) {
      } else {
        console.error('❌ Fehler beim Aktualisieren im Backend:', result.error);
        // Bei Fehler: Daten neu laden um Konsistenz zu gewährleisten
        await loadArticles();
      }
    } catch (error) {
      console.error('❌ Fehler beim API-Call:', error);
      // Bei Fehler: Daten neu laden um Konsistenz zu gewährleisten
      await loadArticles();
    }
  }, [loadArticles]);

  const addArticle = useCallback((newArticle) => {
    setArticles(prevArticles => {
      // Prüfen ob Artikel bereits existiert (basierend auf Name)
      const existingIndex = prevArticles.findIndex(a => a.name === newArticle.name);
      if (existingIndex >= 0) {
        // Artikel existiert bereits - ersetzen
        const updated = [...prevArticles];
        updated[existingIndex] = { ...updated[existingIndex], ...newArticle };
        return updated;
      } else {
        // Neuer Artikel - hinzufügen
        return [...prevArticles, newArticle];
      }
    });
  }, []);

  const removeArticle = useCallback((articleId) => {
    setArticles(prevArticles => 
      prevArticles.filter(article => article.id !== articleId)
    );
  }, []);

  const getArticleById = useCallback((articleId) => {
    return articles.find(article => article.id === articleId);
  }, [articles]);

  const getArticleByName = useCallback((articleName) => {
    return articles.find(article => article.name === articleName);
  }, [articles]);

  // Socket.io Event-Listener für Artikel-Updates
  useEffect(() => {
    let isMounted = true;
    
    // Socket.io initialisieren (nur wenn noch nicht initialisiert)
    const initializeSocket = async () => {
      try {
        await initSocket();
      } catch (error) {
        console.error('Socket initialization failed:', error);
      }
    };
    
    initializeSocket();

    // Artikel-Update Event
    const handleArticleUpdated = (updatedArticle) => {
      if (!isMounted) return;
      setArticles(prevArticles => 
        prevArticles.map(article => 
          article.id === updatedArticle.id 
            ? { ...article, ...updatedArticle }
            : article
        )
      );
    };

    // Artikel-Erstellt Event
    const handleArticleCreated = (newArticle) => {
      if (!isMounted) return;
      setArticles(prevArticles => {
        const existingIndex = prevArticles.findIndex(a => a.name === newArticle.name);
        if (existingIndex >= 0) {
          const updated = [...prevArticles];
          updated[existingIndex] = { ...updated[existingIndex], ...newArticle };
          return updated;
        } else {
          return [...prevArticles, newArticle];
        }
      });
    };

    socket.on('article-updated', handleArticleUpdated);
    socket.on('article-created', handleArticleCreated);

    // Cleanup
    return () => {
      isMounted = false;
      socket.off('article-updated', handleArticleUpdated);
      socket.off('article-created', handleArticleCreated);
    };
  }, []); // Keine Abhängigkeiten mehr!

  const value = {
    articles,
    loading,
    loadArticles,
    updateArticle,
    addArticle,
    removeArticle,
    getArticleById,
    getArticleByName,
  };

  return (
    <ArticleContext.Provider value={value}>
      {children}
    </ArticleContext.Provider>
  );
};
